import { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import Navigation from "@/components/site/Navigation";
import Hero from "@/components/site/Hero";
import Philosophy from "@/components/site/Manifesto";
import About from "@/components/site/Founder";
import GatheringsTeaser from "@/components/site/GatheringsTeaser";
import Contact from "@/components/site/Contact";
import Footer from "@/components/site/Footer";
import CinematicIntro from "@/components/site/CinematicIntro";
import AnnouncementModal from "@/components/site/AnnouncementModal";
import { scrollToId } from "@/lib/useSectionNav";

// Module-level flag: intro plays on every fresh page load, but not on
// client-side navigations back to Home within the same session.
let introPlayedOnce = false;

export default function Home() {
  const location = useLocation();
  // Skip the intro when deep-linking to a section (e.g. arriving to reserve / view a section).
  const [intro, setIntro] = useState(!introPlayedOnce && !location.state?.scrollTo);

  useEffect(() => {
    introPlayedOnce = true;
    const replay = () => setIntro(true);
    window.addEventListener("gaia:replay-intro", replay);
    return () => window.removeEventListener("gaia:replay-intro", replay);
  }, []);

  // Scroll to a requested section when arriving from another route.
  // Re-run as the layout settles (images loading above the target shift its
  // position), so we reliably land on the right section (e.g. the contact form).
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    let cancelled = false;
    const doScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(target);
      if (el) scrollToId(target, { duration: 0.8 });
    };
    const timers = [200, 600, 1100, 1800, 2600].map((t) => setTimeout(doScroll, t));
    window.addEventListener("load", doScroll);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      window.removeEventListener("load", doScroll);
    };
  }, [location]);

  const enter = () => setIntro(false);

  return (
    <>
      <AnimatePresence>
        {intro && <CinematicIntro key="intro" onEnter={enter} />}
      </AnimatePresence>

      <main className="relative bg-gaia-bg" data-testid="home-page">
        <Navigation />
        <Hero />
        <Philosophy />
        <About />
        <GatheringsTeaser />
        <Contact />
        <Footer />
      </main>

      <AnnouncementModal ready={!intro} />
    </>
  );
}
